/**
 * Safe Area Management for DopeOS
 * 
 * CANONICAL SAFE AREA SYSTEM:
 * 
 * The safe area defines the usable space within the CRT display where content
 * should be rendered. This system provides consistent positioning and scaling
 * across all views while preserving the calibrated CRT visual appearance.
 * 
 * KEY PRINCIPLES:
 * 1. DopeOS content uses the FULL buffer space (not phosphor bounds)
 * 2. All views receive the same safe area calculation for consistency
 * 3. CRT profiles remain untouched - only safe area logic changes
 * 4. Content should scale proportionally within the safe area
 * 
 * USAGE:
 * - Call getCanonicalSafeArea(buffer) to get consistent safe area bounds
 * - Use the returned dimensions for all content positioning
 * - Scale content proportionally to fit within these bounds
 */

// Note: pickCRTProfile was removed as safe area now uses full buffer space consistently

/**
 * Get the canonical safe area for DopeOS content
 * 
 * This function returns the full buffer space as the safe area,
 * ensuring consistent behavior across all views and maximum
 * utilization of the available screen real estate.
 * 
 * @param {Object} buffer - Buffer object with w, h properties
 * @returns {Object} Safe area bounds: { left, top, right, bottom, width, height }
 */
export function getCanonicalSafeArea(buffer) {
  if (!buffer || !buffer.w || !buffer.h) {
    console.warn('[safeArea] Invalid buffer passed to getCanonicalSafeArea');
    return { left: 0, top: 0, right: 640, bottom: 400, width: 640, height: 400 };
  }

  // Use the buffer dimensions as-is; DopeOS canvas already reflects any
  // bufferScale applied at profile level.
  return {
    left: 0,
    top: 0,
    right: buffer.w,
    bottom: buffer.h,
    width: buffer.w,
    height: buffer.h
  };
}

/**
 * Calculate optimal content scaling within safe area
 * 
 * Provides consistent scaling calculations for content that needs
 * to fit within the safe area while maintaining proportions.
 * 
 * @param {Object} safeArea - Safe area bounds from getCanonicalSafeArea
 * @param {Object} contentSize - Content dimensions { width, height }
 * @param {Object} options - Scaling options
 * @param {number} options.minScale - Minimum allowed scale (default: 1.0)
 * @param {number} options.maxScale - Maximum allowed scale (default: 4.0)
 * @param {number} options.paddingRatio - Padding as ratio of safe area (default: 0.05)
 * @param {number} options.contentRatio - Ratio of safe area to use for content (default: 0.85)
 * @returns {Object} Scaling info: { scale, scaledWidth, scaledHeight }
 */
export function calculateOptimalScale(safeArea, contentSize, options = {}) {
  const {
    minScale = 1.0,
    maxScale = 4.0,
    paddingRatio = 0.05,
    contentRatio = 0.85
  } = options;

  // Calculate available space with padding
  const padding = Math.min(safeArea.width, safeArea.height) * paddingRatio;
  const availableWidth = (safeArea.width - padding * 2) * contentRatio;
  const availableHeight = (safeArea.height - padding * 2) * contentRatio;

  // Calculate scale to fit within available space
  const scaleByWidth = availableWidth / contentSize.width;
  const scaleByHeight = availableHeight / contentSize.height;
  const scale = Math.max(minScale, Math.min(scaleByWidth, scaleByHeight, maxScale));

  return {
    scale,
    scaledWidth: contentSize.width * scale,
    scaledHeight: contentSize.height * scale
  };
}

/**
 * Center content within safe area
 * 
 * Calculates centered position for content within the safe area bounds.
 * 
 * @param {Object} safeArea - Safe area bounds
 * @param {Object} contentSize - Content dimensions { width, height }
 * @returns {Object} Centered position: { x, y }
 */
export function centerInSafeArea(safeArea, contentSize) {
  return {
    x: safeArea.left + (safeArea.width - contentSize.width) / 2,
    y: safeArea.top + (safeArea.height - contentSize.height) / 2
  };
}

// Legacy function removed - use getCanonicalSafeArea instead

/**
 * Get safe area boundaries as CSS-style rect
 * 
 * @param {Object} buffer - Buffer object
 * @returns {Object} CSS-style rect: { x, y, width, height }
 */
export function getSafeAreaRect(buffer) {
  const safeArea = getCanonicalSafeArea(buffer);
  return {
    x: safeArea.left,
    y: safeArea.top,
    width: safeArea.width,
    height: safeArea.height
  };
}

/**
 * Utility to draw safe area debug overlay
 * 
 * Renders a visual overlay showing the safe area bounds.
 * Use only for debugging - remove in production.
 * 
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} buffer - Buffer object
 * @param {string} color - Overlay color (default: 'rgba(255, 0, 255, 0.2)')
 */
export function drawSafeAreaDebug(ctx, buffer, color = 'rgba(255, 0, 255, 0.28)') {
  const safeArea = getCanonicalSafeArea(buffer);
  const { left, top, width, height } = safeArea;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;

  for (let x = left; x <= left + width; x += 8) {
    ctx.beginPath();
    ctx.moveTo(x + 0.5, top);
    ctx.lineTo(x + 0.5, top + height);
    ctx.stroke();
  }
  for (let y = top; y <= top + height; y += 16) {
    ctx.beginPath();
    ctx.moveTo(left, y + 0.5);
    ctx.lineTo(left + width, y + 0.5);
    ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(255, 0, 255, 0.9)';
  ctx.lineWidth = 1;
  ctx.strokeRect(left + 8.5, top + 8.5, width - 17, height - 17);
  ctx.restore();
}
